"use client";

import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Globe, Building2, TrendingUp, Check } from "lucide-react";

const packages = [
  {
    id: 1,
    title: "Free Zone Company Setup",
    description: "Best for foreign investors and entrepreneurs seeking 100% ownership without needing a local partner. Free zones are designed for specific business activities and offer tax and regulatory benefits.",
    icon: <Globe size={22} className="stroke-[1.5]" />,
    benefits: [
      "100% foreign ownership",
      "No personal or corporate taxes",
      "Simplified import/export procedures",
      "Access to modern infrastructure",
    ],
    startingCost: "AED 12,900",
    costHighlight: true,
    ctaLink: "/free-zone",
    ctaLabel: "Explore Free Zone",
  },
  {
    id: 2,
    title: "Mainland Company Setup",
    description: "A mainland company allows you to operate anywhere in the UAE, including outside free zones, and provides access to a larger local market. You may need a local sponsor or service agent depending on your business type.",
    icon: <Building2 size={22} className="stroke-[1.5]" />,
    benefits: [
      "Freedom to trade anywhere in UAE",
      "Can do government contracts",
      "Flexibility in business activities",
      "Office space options available",
    ],
    startingCost: "AED 18,000",
    costHighlight: false,
    ctaLink: "/mainland",
    ctaLabel: "Explore Mainland",
  },
  {
    id: 3,
    title: "Offshore Company Setup",
    description: "Ideal for investors wanting a legal business entity in the UAE for international trade, holding assets, or investment purposes, without the need to physically operate in the UAE.",
    icon: <TrendingUp size={22} className="stroke-[1.5]" />,
    benefits: [
      "No requirement to reside in UAE",
      "Tax efficiency and confidentiality",
      "Easy repatriation of profits",
      "Useful for asset protection models",
    ],
    startingCost: "AED 18,500",
    costHighlight: false,
    ctaLink: "/offshore",
    ctaLabel: "Explore Offshore",
  },
];

export default function BusinessPackages() {
  const router = useRouter();

  return (
    <section className="bg-[#FAF9F6] py-20 md:py-28 border-b border-[#EAE6DF] w-full overflow-x-hidden text-[#111111] antialiased">
      <div className="w-full mx-auto max-w-[1300px] xl:max-w-[1500px] 2xl:max-w-[1700px] px-4 sm:px-6 md:px-8">
        
        {/* ── HEADER SECTION (Clean Split Editorial Layout) ── */}
        <div className="mb-16 md:mb-24 flex flex-col items-start justify-center text-center border-b border-[#EAE6DF] pb-8 lg:flex-row lg:items-end gap-6">
          <div className="max-w-2xl">
            <p className="text-[#B8893A] text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-3 block">
              Business Solutions
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-light tracking-tight leading-[1.15] text-black">
              Exclusive <span className="font-serif font-normal text-[#B8893A]">Packages</span>
            </h2>
          </div>
         
        </div>

        {/* ── HIGH-END LUXURY CORPORATE GRID ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EAE6DF] border border-[#EAE6DF]">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.05, ease: "easeOut" }}
              className={`flex flex-col justify-between p-6 sm:p-8 xl:p-10 transition-all duration-300 relative group min-h-[580px] ${
                pkg.costHighlight ? "bg-[#FAF9F6]" : "bg-white hover:bg-[#FAF9F6]"
              }`}
            >
              {/* Top Section */}
              <div>
                {/* Header Flag / Badge Counter */}
                <div className="flex justify-between items-center mb-8">
                  <div className={`w-10 h-10 border flex items-center justify-center transition-all duration-400 ${
                    pkg.costHighlight 
                      ? "bg-[#111111] text-white border-[#111111]" 
                      : "bg-white text-[#B8893A] border-[#EAE6DF] group-hover:bg-[#111111] group-hover:text-white group-hover:border-[#111111]"
                  }`}>
                    {pkg.icon}
                  </div>
                  {pkg.costHighlight && (
                    <span className="text-[9px] font-bold tracking-[0.2em] bg-[#B8893A] text-white px-3 py-1 uppercase">
                      Most Popular
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-medium tracking-tight text-black mb-4">
                  {pkg.title}
                </h3>

                {/* Description */}
                <p className="text-gray-500 font-light text-sm leading-relaxed mb-8">
                  {pkg.description}
                </p>

                {/* Benefits List */}
                <div className="space-y-3.5 mb-8">
                  {pkg.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3.5 group/item">
                      <div className="w-4 h-4 rounded-full border border-[#B8893A]/40 flex items-center justify-center text-[#B8893A] mt-0.5 shrink-0 bg-white">
                        <Check size={10} className="stroke-[3]" />
                      </div>
                      <span className="text-gray-600 text-sm font-light tracking-wide">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Section (Sticky Footprint Matrix) */}
              <div className="border-t border-gray-100 pt-6 mt-auto">
                <div className="flex items-baseline justify-between mb-6">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                    Investment Base
                  </span>
                  <div className="text-right">
                    <span className="text-xs font-light text-gray-400 block -mb-1">From</span>
                    <span className="text-2xl font-bold tracking-tight text-black">
                      {pkg.startingCost}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => router.push(pkg.ctaLink)}
                  className={`w-full py-4 text-xs font-bold uppercase tracking-[0.2em] rounded-none transition-all duration-300 ${
                    pkg.costHighlight
                      ? "bg-[#111111] text-white hover:bg-[#B8893A]"
                      : "border border-[#111111] text-[#111111] hover:bg-[#111111] hover:text-white"
                  }`}
                >
                  Explore Mandate
                </button>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}